import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';
import { OrderReviewExportCheckBox, OrderReviewExportOptions } from '../../api';
import { FileResult, UnitOfMeasureOrderQuantity } from '../../api/models/api-shared.models';
import {
  CustomerProductPriceResult,
  OrderReviewCustomerProductPriceResult,
} from '../../api/models/customer-product-price.models';
import {
  DidYouForgetCustomerProducts,
  GetDidYouForgetCustomerProductsRequest,
  GetDidYouForgetCustomerProductsResult,
  UpdateCustomProductRequest,
} from '../../api/models/customer-product.models';
import {
  GetOrderEntrySearchResultDetail,
  ProductListCategoryResult,
  ProductListOrderEntrySearchRequest,
} from '../../api/models/order-entry-search.models';
import { ProductDetailTransfer, USDAProductStatusTypeData } from '../../api/models/product-detail.models';
import { ProductListProduct } from '../../api/models/product-list-search.models';
import {
  getAllProducts,
  getProductIsLowInventory,
  isShipsSeperatelyProduct,
  normalizeProductKey,
  productHasQuantity,
  updateProductCustomAttributesFromPayload,
} from '../../helpers/general/product';
import { getNormalizedProductListGridData } from '../../helpers/general/product-list';
import { OrderEntryType } from '../../models/order.enums';
import { RootState } from '../store';

// Adapters
const productsAdapter = createEntityAdapter<ProductListProduct>({
  selectId: (product: ProductListProduct) => normalizeProductKey(product.ProductKey),
});

const didYouForgetCustomerProductsAdapter = createEntityAdapter<DidYouForgetCustomerProducts>({
  selectId: (product: DidYouForgetCustomerProducts) => normalizeProductKey(product.ProductKey),
});

// State
export interface OrderReviewState {
  apiRequest: ProductListOrderEntrySearchRequest | undefined;
  products: EntityState<ProductListProduct>;
  productListLoading: boolean;
  didYouForgetCustomerProducts: EntityState<DidYouForgetCustomerProducts>;
  didYouForgetCustomerProductsLoading: boolean;
  didYouForgetCustomerProductApiRequest: GetDidYouForgetCustomerProductsRequest | undefined;
  didYouForgetProductPricesLoaded?: boolean;
  orderReviewReportFileData: FileResult | undefined;
  orderReviewReportLoading: boolean;
  orderReviewExportOptions?: OrderReviewExportOptions;
  orderReviewLabel?: string[];
  resetOrderReviewExport: boolean;
  orderReviewExportOrderedList?: OrderReviewExportCheckBox[];
}

const initialState: OrderReviewState = {
  apiRequest: undefined,
  products: productsAdapter.getInitialState(),
  productListLoading: false,
  didYouForgetCustomerProducts: didYouForgetCustomerProductsAdapter.getInitialState(),
  didYouForgetCustomerProductsLoading: false,
  didYouForgetCustomerProductApiRequest: undefined,
  didYouForgetProductPricesLoaded: undefined,
  orderReviewReportFileData: undefined,
  orderReviewReportLoading: false,
  orderReviewLabel: undefined,
  resetOrderReviewExport: false,
  orderReviewExportOrderedList: undefined,
};

// Reducers
export const orderReviewSlice = createSlice({
  name: 'orderReview',
  initialState: initialState,
  reducers: {
    resetOrderReviewState: () => {
      return initialState;
    },
    setApiRequest: (state: OrderReviewState, action: PayloadAction<ProductListOrderEntrySearchRequest | undefined>) => {
      state.apiRequest = action.payload;
    },
    searchProductListOrderReview: (state: OrderReviewState, action: PayloadAction<ProductListCategoryResult>) => {
      const data = getNormalizedProductListGridData(action.payload.ProductListCategories);
      productsAdapter.setAll(state.products, data.products);
    },
    setProducts: (state: OrderReviewState, action: PayloadAction<GetOrderEntrySearchResultDetail>) => {
      const products = getAllProducts(action.payload);
      productsAdapter.setAll(state.products, products as ProductListProduct[]);
    },
    setDidYouForgetCustomerProducts: (
      state: OrderReviewState,
      action: PayloadAction<GetDidYouForgetCustomerProductsResult>
    ) => {
      const didYouForgetCustomerProducts = action.payload.Products;
      didYouForgetCustomerProductsAdapter.setAll(
        state.didYouForgetCustomerProducts,
        didYouForgetCustomerProducts as DidYouForgetCustomerProducts[]
      );
    },
    resetDidYouForgetCustomerProducts: (state: OrderReviewState) => {
      didYouForgetCustomerProductsAdapter.removeAll(state.didYouForgetCustomerProducts);
    },
    setProductListLoading: (state: OrderReviewState, action: PayloadAction<boolean>) => {
      state.productListLoading = action.payload;
    },
    didYouForgetCustomerProductsLoading: (state: OrderReviewState, action: PayloadAction<boolean>) => {
      state.didYouForgetCustomerProductsLoading = action.payload;
    },
    updateProduct: (state: OrderReviewState, action: PayloadAction<ProductDetailTransfer>) => {
      const product = state.products.entities[normalizeProductKey(action.payload.productKey)];
      if (product) {
        product.UnitOfMeasureOrderQuantities.forEach((uom: UnitOfMeasureOrderQuantity) => {
          if (uom.UnitOfMeasure === action.payload.uom) {
            uom.ExtendedPrice = action.payload.extendedPrice;
            uom.Quantity = action.payload.quantity;
            uom.ShowOrderQuantityAlert = action.payload.showOrderQuantityAlert;
          }
        });
      }
      const didYouForgetCustomerProduct =
        state.didYouForgetCustomerProducts.entities[normalizeProductKey(action.payload.productKey)];
      if (didYouForgetCustomerProduct) {
        didYouForgetCustomerProduct.UnitOfMeasureOrderQuantities.forEach((uom: UnitOfMeasureOrderQuantity) => {
          if (uom.UnitOfMeasure === action.payload.uom) {
            uom.ExtendedPrice = action.payload.extendedPrice;
            uom.Quantity = action.payload.quantity;
            uom.ShowOrderQuantityAlert = action.payload.showOrderQuantityAlert;
          }
        });
        productsAdapter.addOne(state.products, didYouForgetCustomerProduct);
      }
    },
    updateProductCustomAttributes: (state: OrderReviewState, action: PayloadAction<UpdateCustomProductRequest>) => {
      const product = state.products.entities[normalizeProductKey(action.payload.ProductKey)];
      if (!product) return;

      updateProductCustomAttributesFromPayload(product, action.payload);
    },
    removeOrderReviewProduct: (state: OrderReviewState, action: PayloadAction<string>) => {
      productsAdapter.removeOne(state.products, normalizeProductKey(action.payload));
    },
    addOrderReviewProduct: (state: OrderReviewState, action: PayloadAction<ProductListProduct>) => {
      productsAdapter.addOne(state.products, action.payload);
    },
    setProductPrices: (state: OrderReviewState, action: PayloadAction<CustomerProductPriceResult[]>) => {
      action.payload.forEach((priceResult: CustomerProductPriceResult) => {
        if (priceResult.ProductKey) {
          const product = state.products.entities[normalizeProductKey(priceResult.ProductKey)];
          if (product) {
            product.PriceLoaded = true;
            product.UnitOfMeasureOrderQuantities.forEach((uom: UnitOfMeasureOrderQuantity) => {
              if (uom.UnitOfMeasure === priceResult.UnitOfMeasureType) {
                uom.Price = priceResult.Price;
                if (priceResult.SecondaryUnitPrice) {
                  uom.SecondaryUnitPrice = priceResult.SecondaryUnitPrice;
                }
                if (priceResult.ExtendedPrice) {
                  uom.ExtendedPrice = priceResult.ExtendedPrice;
                }
                if (priceResult.ProductAverageWeight > 0) {
                  uom.ProductAverageWeight = priceResult.ProductAverageWeight;
                }
              }
            });
          }
        }
      });
    },
    setDidYouForgetCustomerProductPrices: (
      state: OrderReviewState,
      action: PayloadAction<OrderReviewCustomerProductPriceResult[]>
    ) => {
      action.payload.forEach((priceResult: OrderReviewCustomerProductPriceResult) => {
        if (priceResult.ProductKey) {
          const product = state.didYouForgetCustomerProducts.entities[normalizeProductKey(priceResult.ProductKey)];
          if (product) {
            product.PriceLoaded = true;
            product.UnitOfMeasureOrderQuantities.forEach((uom: UnitOfMeasureOrderQuantity) => {
              if (uom.UnitOfMeasure === priceResult.UnitOfMeasureType) {
                uom.Price = priceResult.Price;
                if (priceResult.SecondaryUnitPrice !== undefined) {
                  uom.SecondaryUnitPrice = priceResult.SecondaryUnitPrice;
                }
                if (priceResult.ProductAverageWeight > 0) {
                  uom.ProductAverageWeight = priceResult.ProductAverageWeight;
                }
              }
            });
          }
        }
      });
    },
    setDidYouForgetApiRequest: (
      state: OrderReviewState,
      action: PayloadAction<GetDidYouForgetCustomerProductsRequest | undefined>
    ) => {
      state.didYouForgetCustomerProductApiRequest = action.payload;
    },
    setDidYouForgetProductPriceLoaded: (state: OrderReviewState, action: PayloadAction<boolean>) => {
      state.didYouForgetProductPricesLoaded = action.payload;
    },
    resetDidYouForgetProductPriceLoaded: (state: OrderReviewState) => {
      state.didYouForgetProductPricesLoaded = initialState.didYouForgetProductPricesLoaded;
    },
    setOrderReviewUsdaProductStatusType: (
      state: OrderReviewState,
      action: PayloadAction<USDAProductStatusTypeData[]>
    ) => {
      action.payload.forEach((statusResult: USDAProductStatusTypeData) => {
        if (statusResult.ProductKey) {
          const product = state.products.entities[normalizeProductKey(statusResult.ProductKey)];
          if (product) {
            product.USDAProductStatusType = statusResult.USDAProductStatusType;
          }
        }
      });
    },
    setDidYouForgetUsdaProductStatusType: (
      state: OrderReviewState,
      action: PayloadAction<USDAProductStatusTypeData[]>
    ) => {
      action.payload.forEach((statusResult: USDAProductStatusTypeData) => {
        if (statusResult.ProductKey) {
          const didYouForgetProduct =
            state.didYouForgetCustomerProducts.entities[normalizeProductKey(statusResult.ProductKey)];
          if (didYouForgetProduct) {
            didYouForgetProduct.USDAProductStatusType = statusResult.USDAProductStatusType;
          }
        }
      });
    },
    setOrderReviewReportLoading: (state: OrderReviewState, action: PayloadAction<boolean>) => {
      state.orderReviewReportLoading = action.payload;
    },
    resetOrderReviewReportLoaded: (state: OrderReviewState) => {
      state.orderReviewReportLoading = initialState.orderReviewReportLoading;
    },
    setOrderReviewReportFileData: (state: OrderReviewState, action: PayloadAction<FileResult>) => {
      state.orderReviewReportFileData = action.payload;
    },
    setOrderReviewLabel: (state: OrderReviewState, action: PayloadAction<string[]>) => {
      state.orderReviewLabel = action.payload;
    },
    setOrderReviewExportOptions: (state: OrderReviewState, action: PayloadAction<OrderReviewExportOptions>) => {
      state.orderReviewExportOptions = action.payload;
    },
    resetOrderReviewExport: (state: OrderReviewState, action: PayloadAction<boolean>) => {
      state.resetOrderReviewExport = action.payload;
    },
    setOrderReviewExportOrderedList: (state: OrderReviewState, action: PayloadAction<OrderReviewExportCheckBox[]>) => {
      state.orderReviewExportOrderedList = action.payload;
    },
    resetOrderReviewExportOptions: (state: OrderReviewState) => {
      state.orderReviewExportOptions = initialState.orderReviewExportOptions;
    },
  },
});

// Selectors
export const { selectAll: selectAllOrderReviewProducts, selectById: selectOrderReviewProductById } =
  productsAdapter.getSelectors<RootState>((state: RootState) => state.orderReview.products);

export const selectOrderReviewActionNeededProducts = createSelector(
  [selectAllOrderReviewProducts, (state: RootState) => state.orders.order?.OrderType],
  (products, orderType) =>
    products.filter((p) => {
      // For edit orders only ignore !CanOrder items with quantity
      if (
        orderType === OrderEntryType.Edit &&
        !p.CanOrder &&
        p.UnitOfMeasureOrderQuantities.some((u) => u.Quantity > 0)
      ) {
        return false;
      }
      return !p.CanOrder || p.IsDiscontinued;
    })
);

const selectProductInventoriesDictionary = (state: RootState) => state.orders.productInventoryData;
export const selectOrderReviewActionRequestedProducts = createSelector(
  selectAllOrderReviewProducts,
  selectProductInventoriesDictionary,
  (products, productInventories) => {
    return products.filter(
      (p) =>
        !(isShipsSeperatelyProduct(p) && productHasQuantity(p)) &&
        (getProductIsLowInventory(productInventories.entities, p) ||
          p.UnitOfMeasureOrderQuantities.some((uom: UnitOfMeasureOrderQuantity) => uom.ShowOrderQuantityAlert))
    );
  }
);

export const selectOrderReviewEditedProducts = createSelector(selectAllOrderReviewProducts, (products) =>
  products.filter((p) => p.UnitOfMeasureOrderQuantities.some((u) => (u.EditOriginalQuantity ?? 0) != u.Quantity))
);

export const selectHasOrderReviewActionNeededProducts = createSelector(
  selectOrderReviewActionNeededProducts,
  (products) => products.length > 0
);

export const selectHasOrderReviewActionRequestedProducts = createSelector(
  selectOrderReviewActionRequestedProducts,
  (products) => products.length > 0
);

export const selectHasOrderReviewEditedProducts = createSelector(
  selectOrderReviewEditedProducts,
  (products) => products.length > 0
);

export const selectOrderReviewShipsSeparatelyProducts = createSelector(selectAllOrderReviewProducts, (products) =>
  products.filter((p) => isShipsSeperatelyProduct(p) && productHasQuantity(p))
);

export const selectHasOrderReviewShipsSeparatelyProducts = createSelector(
  selectOrderReviewShipsSeparatelyProducts,
  (products) => products.length > 0
);

export const { selectAll: selectAllDidYouForgetCustomerProducts } =
  didYouForgetCustomerProductsAdapter.getSelectors<OrderReviewState>(
    (state: OrderReviewState) => state.didYouForgetCustomerProducts
  );

export const isOrderReviewProductsPagePricesLoaded = createSelector(
  selectAllOrderReviewProducts,
  (products) =>
    products.flatMap((p) =>
      p.UnitOfMeasureOrderQuantities.map((uom) => ({ product: p, uom })).filter(
        (data) => data.uom.Price || p.PriceLoaded
      )
    ).length > 0
);

export const didYouForgetProductsPagePricesLoaded = createSelector(
  (state: RootState) => selectAllDidYouForgetCustomerProducts(state.orderReview),
  (products) => products.filter((p) => p.PriceLoaded).length > 0
);

export const isReviewProductsPagePricesLoaded = createSelector(
  [isOrderReviewProductsPagePricesLoaded, didYouForgetProductsPagePricesLoaded],
  (orderReviewHasPrice, didYouForgetHasPrice): boolean => {
    return orderReviewHasPrice || didYouForgetHasPrice;
  }
);
export const selectOrderReviewUsdaProductKeys = createSelector(selectAllOrderReviewProducts, (products) => {
  return products.filter((p) => p.IsUSDAProduct).map((p) => p.ProductKey ?? '');
});

export const selectDidYouForgetUsdaProductKeys = createSelector(
  selectAllDidYouForgetCustomerProducts,
  (didYouForgetProducts) => {
    return didYouForgetProducts.filter((p) => p.IsUSDAProduct).map((p) => p.ProductKey ?? '');
  }
);
